import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { clearToken } from '../api/superAdminClient';
import toast from 'react-hot-toast';

interface LayoutProps {
  children: ReactNode;
  title: string;
}

/**
 * Layout 元件（包含側邊欄和導航）
 */
export function Layout({ children, title }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    clearToken();
    toast.success('已登出');
    navigate('/login');
  };

  const menuItems = [
    { path: '/dashboard', label: '儀表板', icon: '📊' },
    { path: '/tenants', label: '租戶管理', icon: '🏢' },
    { path: '/feature-flags', label: 'Feature Flags', icon: '🚩' },
    { path: '/quotas', label: '配額管理', icon: '📊' },
    { path: '/audit-logs', label: '稽核日誌', icon: '📝' },
    { path: '/lite', label: 'LITE 群組', icon: '💬' },
    { path: '/lite-ads', label: 'LITE 廣告', icon: '📢' },
    { path: '/system-settings', label: '系統設定', icon: '⚙️' },
    { path: '/tool-station-visible', label: '工具站顯示', icon: '🛠️' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 側邊欄 */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white shadow-lg">
        <div className="p-6 border-b">
          <h1 className="text-2xl font-bold text-primary">Super Admin</h1>
        </div>
        <nav className="p-4 space-y-2">
          {menuItems.map(item => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors
                  ${
                    isActive
                      ? 'bg-primary text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }
                `}
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="absolute bottom-4 left-4 right-4">
          <button
            onClick={handleLogout}
            className="w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            登出
          </button>
        </div>
      </aside>

      {/* 主內容區 */}
      <div className="ml-64">
        <header className="bg-white shadow-sm px-6 py-4">
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        </header>
        <main className="p-6">{children}</main>
      </div>
    </div>
  );
}
